/**
 * DetailExtraSections — v2 상세 추가 섹션 (2026-09-21 배치 5).
 *   - DealCompareSection : 인근 실거래 비교 (type_prices 요약값, 값 있는 타입이 1개 이상일 때만)
 *   - LocationPointsSection : 입지 4카드 (detailed_description [교통]/[학군]/[생활]/[호재] 파싱, 0건이면 미렌더)
 *   - ModelHouseSection : 모델하우스 주소 텍스트 + 복사 (외부 링크·길찾기 없음, 대표 결정 #4)
 *   - DetailNotice : 하단 고지 문구
 * 모두 외부(http) 링크를 만들지 않는다.
 */

import React, { useState } from 'react';
import type { DealCompareRow } from '../../domain/property/detailV2';
import type { LocationPoint, LocationKey } from '../../domain/property/locationPoints';
import { SectionCard } from '../../design-system/components/section/SectionCard';
import {
  TrendIcon,
  CityIcon,
  StoreIcon,
  TrainIcon,
  SchoolIcon,
  BagIcon,
  LandmarkIcon,
} from '../../design-system/components/section/SectionIcons';

// ─────────────────────────────────────────────
// 인근 실거래 비교
// ─────────────────────────────────────────────
export const DealCompareSection: React.FC<{ rows: DealCompareRow[] }> = ({ rows }) => {
  if (rows.length === 0) return null;
  return (
    <SectionCard title="인근 실거래 비교" icon={<TrendIcon />} action={<span className="text-[11px] font-bold text-mute2">국토부 실거래가</span>}>
      <div className="flex flex-col gap-3">
        {rows.map((r) => {
          const cheaper = r.pct < 0;
          return (
            <div key={r.type} className="flex flex-col gap-1.5 rounded-ca-xl bg-ca-surface px-3 py-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[14px] text-body">{r.type} 최근 실거래</span>
                <b className="num text-[15px] text-ink">{r.recentDeal}</b>
              </div>
              <div className="flex items-center justify-between">
                <span className="num text-[13px] text-muted">이 단지 분양가 {r.price}</span>
                <span
                  className={`num rounded-ca-sm px-2 py-1 text-[11px] font-bold ${
                    cheaper ? 'bg-ok-bg text-[#047857]' : 'bg-ca-surface text-[#475569]'
                  }`}
                >
                  {r.pct > 0 ? '+' : ''}
                  {r.pct}% {cheaper ? '저렴' : ''}
                </span>
              </div>
              <span className="num text-[11px] font-bold text-mute2">
                거래 {r.dealCount}건{r.dealDate ? ` · ${r.dealDate} 기준` : ''}
              </span>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

// ─────────────────────────────────────────────
// 입지 4카드
// ─────────────────────────────────────────────
const LOC_STYLE: Record<LocationKey, { icon: React.ReactNode; color: string }> = {
  교통: { icon: <TrainIcon />, color: 'var(--ca-info)' },
  학군: { icon: <SchoolIcon />, color: 'var(--ca-accent)' },
  생활: { icon: <BagIcon />, color: 'var(--ca-ok)' },
  호재: { icon: <LandmarkIcon />, color: 'var(--v2-violet)' },
};

export const LocationPointsSection: React.FC<{ points: LocationPoint[] }> = ({ points }) => {
  if (points.length === 0) return null;
  return (
    <SectionCard title="입지" icon={<CityIcon />}>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4" data-testid="location-points">
        {points.map((p) => (
          <div key={p.key} className="flex flex-col gap-1.5 rounded-ca-xl bg-ca-surface p-3">
            <span className="flex" style={{ color: LOC_STYLE[p.key].color }}>
              {LOC_STYLE[p.key].icon}
            </span>
            <b className="text-[14px] font-bold text-ink">{p.key}</b>
            <span className="text-[13px] leading-[18px] text-muted">{p.text}</span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

// ─────────────────────────────────────────────
// 모델하우스 — 주소 텍스트 + 복사만 (길찾기·카카오맵·티맵 링크 금지)
// ─────────────────────────────────────────────
export const ModelHouseSection: React.FC<{ address?: string | null }> = ({ address }) => {
  const [copied, setCopied] = useState(false);
  const text = (address ?? '').trim();
  if (!text) return null;

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <SectionCard title="모델하우스" icon={<StoreIcon />}>
      <div className="flex items-center justify-between gap-2" data-testid="model-house">
        <b className="min-w-0 break-keep text-[15px] font-semibold leading-[22px] text-ink">{text}</b>
        <button
          type="button"
          onClick={copy}
          className="inline-flex h-[34px] shrink-0 items-center gap-1 rounded-ca-lg border border-ca-line bg-frame px-3 text-[12px] font-bold text-body"
        >
          {copied ? '복사됨' : '주소 복사'}
        </button>
      </div>
    </SectionCard>
  );
};

// ─────────────────────────────────────────────
// 고지
// ─────────────────────────────────────────────
export const DetailNotice: React.FC = () => (
  <p className="px-4 pb-4 pt-2 text-[11px] leading-[17px] text-mute2" data-testid="detail-notice">
    ※ 본 페이지의 CG·평면도·기재 사항은 인·허가 및 시공 과정에서 변경될 수 있으며, 분양가·혜택 조건의 세부 적용은
    현장 상담을 통해 최종 확인하시기 바랍니다.
  </p>
);
